// src/pages/Help/data/generalHelpData.js

// Import all function icons
import disomeSeqIcon from "../../../assets/images/eirna/SeqIcons/DISOME SEQ GREEN.png";
import helpDeskImage from "../../../assets/images/eirna/quickstart/quickstart.png";


export const generalHelpSections = {
  helpSectionArray: [
    {
      id: "quickstart",
      title: "Quick start",
      description:
        "A basic image based walk through of some of the main features of EIRNABIO Connect using an example dataset",
      image: helpDeskImage,
      plotDescription: ``,
    },
    {
      id: "help-video",
      title: "Help Video",
      description:
        "A video that walks through common interactions on EIRNABIO Connect",
      videoUrl: "https://www.youtube.com/embed/q5U2BYOekN0?rel=0",
      plotDescription: ``,
    },
    {
      id: "disome-seq-guide",
      title: "Disome-seq guide",
      description:
        "A guide to analyzing disosome-seq samples in EIRNABIO Connect",
      image: disomeSeqIcon,
      plotDescription: `
        <h2>Disome-seq</h2>

        ●   Ribosome profiling technique specifically designed to capture and analyze ribosome collisions on mRNAs<br>
        ●   The name "disome-seq" comes from the fact that it isolates and sequences disomes - structures formed when two ribosomes collide on a
        transcript<br>
        ●   During translation, ribosomes move along mRNA to synthesize proteins. However, when something slows down or stalls a leading ribosome
        (due to factors like rare codons, secondary structure, or nascent peptide signals), the following ribosomes can bump into the stalled one,
        forming collided ribosome pairs, or disomes<br>
        ●   The difference between the number of ribosomes protecting the mRNA during nuclease digestion also leads to a difference in the read
        lengths yielded between Ribo-seq and disome-seq.<br>

        <table class="disome_table">
        <tr>
            <td>Monosome (Ribo-seq)</td> 
            <td>● Single ribosome sits on an mRNA strand during translation<br>
                ● During nuclease digestion, only the part of the mRNA that
                is shielded by the ribosome is protected<br>
                ● This protected fragment is ~20-35 nucleotides long,
                corresponding to the the ribosome’s footprint<br>
            </td>
        </tr>

        <tr>
            <td>Disome (Disome-seq) </td>


            <td>● Disome forms when two ribosomes collide<br>
            ● The two ribosomes shield a larger region of mRNA during
            nuclease digestion<br>
            ● Typically the protected fragment is ~45-65 nucleotides
            long, roughly double the monosome footprint<br>
            ● This size reflects the footprint of both ribosomes plus any
            linker region between them that is sterically protected<br>
            </td>
        </tr>

        </table>

        <br>
        <br>
        Below is a summary comparison between Ribo-seq and disome-seq<br><br>

        <table class="disome_table">
        <tr>
            <td>Feature</td> 
            <td>Ribo-seq</td> 
            <td>Disome-seq</td> 
        </tr>

        <tr>
            <td>Aim</td>  
            <td>Identify ribosome positions across transcriptome</td> 
            <td>Detect ribosome collisions</td> 
        </tr>

        <tr>
            <td>Read Length</td>  
            <td>~20-35 nucleotides</td>  
            <td>~45-65 nucleotides</td> 
        </tr>

        <tr>
            <td>Resolution</td>  
            <td>Single-ribosome resolution, high positional accuracy</td> 
            <td>Focused on collision events, slightly lower positional resolution</td> 
        </tr>

        <tr>
            <td>Insight</td>  
            <td>Global translational activity, codon occupancy, pausing</td> 
            <td>Ribosome pile-ups, translation bottlenecks</td> 
        </tr>

        <tr>
            <td>Primary Use Case</td> 
            <td>Measures translation efficiency, identifies pause sites</td> 
            <td>Studies ribosome stalling, collisions and stress responses</td> 
        </tr>

        </table>

        <h2>General Guidelines</h2>


        ● However before we utilise Connect to explore disome-seq data, there are some general guidelines that can be applied across the different
        functionalities.<br><br>
        ● Default read length setting (this does not apply to functions which have a broad read length range by default, e.g subcodon or comparison profile):<br><br>
        <div style="margin-left: 2em;">
            ○ If only Ribo-seq data is selected, default should adjust to Minimum read length: 20 and Maximum read length: 35<br><br>
            ○ If only disome-seq data is selected, default should adjust to Minimum read length: 45 and Maximum read length: 65<br><br>
            ○ If both Ribo-seq and disome-seq data is selected, default should adjust to Minimum read length: 20 and Maximum read length: 65<br><br>
        </div>
        ● We feel the read length range 45-65 is broad enough to capture most reads in a typical disome-seq experiment however these values can
        still be manually adjusted if required<br><br>
        ● An incorrect read length setting will likely result in an error or a plot with sparse/no data. If this does happen we suggest rechecking that the
        min/max read lengths are set to the recommended values.<br><br>


        Lastly, the functions demonstrated in this guideline represent those we believe offer the most insight when using disome-seq data:<br><br>

        <div style="margin-left: 2em;">
            <b>Data Quality:</b> Assesses key metrics to ensure the sequencing data is reliable for downstream analyses<br><br>

            <b>Sub-codon position profile:</b> Analyses read density at subcodon resolution to reveal
            fine-scale patterns in ribosome positioning and translation
            dynamics<br><br>

            <b>Comparison Plot:</b> Visualises differences in read distributions across genes or
            regions between samples or conditions to highlight biological
            variation<br><br>

            <b>Genome Browser:</b> Provides an interactive view of read coverage aligned to genomic features<br><br>

            <b>Pause Prediction:</b> Identifies regions with elevated ribosome occupancy,
            suggesting potential stalling or regulatory pauses in
            translation<br><br>

            <b>Differential Silhouette:</b> Analyses how distinct ribosome occupancy patterns are
            between groups, aiding in the detection of differential translation events<br><br>

            These plots/functions are discussed in terms of disome-seq analysis specifically in the following section.
        </div>

        <h2>Specific plot/functionality examples</h2>
        <h3>Data Quality</h3>

        A number of functions are available here, however we will just focus on four in this guide: Triplet Periodicity, Metagene Profile,Read Length Distribution & Heatmap<br>

        <br><br>
        <h4>Triplet Periodicity</h4><br>

        <div style="margin-left: 2em;">

            ● Ensure the recommended read length settings are applied (Min readlen: 45; Max readlen: 65)<br><br>

            See an example of a triplet periodicity plot for disome-seq samples by clicking <a href="https://eirnabioconnect.com/short/l1V" target="_blank">here</a>

        </div>

        <h4>Metagene Profile</h4><br>

        <div style="margin-left: 2em;">

            ● Ensure the recommended read length settings are applied (Min readlen: 45; Max readlen: 65)<br><br>
            ● Adjust Normalisation method to ‘Normalise by read depth’ or ‘None’<br><br>
            ● The default normalisation method ‘Average read density’ usually does not work with disome-seq data due to the lower number of mapped reads
            (compared to other sequencing data such as Ribo-seq or RNA-seq)<br><br>

            See a metagene profile using disome-seq samples by clicking <a href="https://eirnabioconnect.com/short/l1Z" target="_blank">here</a>
        </div>

        <h4>Read Length Distribution</h4><br>

        <div style="margin-left: 2em;">

            ● Ensure the recommended read length settings are applied (Min readlen: 45; Max readlen: 65)<br><br>

            See a read length distribution plot using disome-seq samples by clicking <a href="https://eirnabioconnect.com/short/l21" target="_blank">here</a>
        </div>

        <h4>Heatmap</h4><br>

        <div style="margin-left: 2em;">

            ● The recommended read length values for disome-seq are Min readlen: 30 and Max readlen: 75<br><br>

            See a heatmap using disome-seq samples by clicking <a href="https://eirnabioconnect.com/short/l22" target="_blank">here</a>

        </div>

        <h3>Sub-codon position profile</h3><br>

        ● The default read length range (Min Read: 20; Max Read: 150) allows for all study inputs to be visualised<br><br>

        ● See a subcodon profile with Ribo-seq only selected <a href="https://eirnabioconnect.com/short/l24" target="_blank">here</a><br>

        ● See a subcodon profile with Disome-seq only selected <a href="https://eirnabioconnect.com/short/l26" target="_blank">here</a><br>

        ● However this functionality is not suited for comparisons between different study types (See a subcodon profile with both Ribo-seq and Disome-seq selected by clicking <a href="https://eirnabioconnect.com/short/l5h" target="_blank">here</a>, note there is no differentiation between Ribo-seq and Disome-seq
        inputs). <br><br>

        ● We instead recommend the ‘Comparison plot’ functionality for comparison between Ribo-seq and disome-seq.<br><br>

        <h3>Comparison Plot</h3><br><br>

        ● The default read length setting (20-150) allows visualisation of all study data.<br><br>
        ● For the normalization method (Advanced Settings), the default is ‘By mapped reads per seq type’<br><br>
        ● Our recommendation would be to adjust this to ‘By mapped reads ignoring seq type’<br><br>
        ● <a href="https://eirnabioconnect.com/short/l2a" target="_blank">Click here</a> to see an example that compares ‘Monosome’ (Ribo-seq) and ‘Disome’ (Disome-seq)<br><br>

        ● Click <a href="https://eirnabioconnect.com/short/l2b" target="_blank">here</a> to see a comparison plot using the same settings but with ‘Monosome_control (Ribo-seq, control samples) and ‘Disome_control’ (Disome-seq, control samples) as inputs<br><br>
        

        ● Note the pause seen before the stop codon in the ‘Disome_control’ data. This pause in SEC61B protein synthesis is not as prominent in the
        monosome data.<br><br>

        ● This known pause before SEC61B protein synthesis completion provides enough time for the recruitment of the membrane insertion
        machinery (Mariappan et al., 2010, Ingolia et al., 2011).<br><br>

        <a href="https://eirnabioconnect.com/short/l2c" target="_blank">Click here</a> to see another example using the same parameters and inputs but instead focuses on the gene XBP1<br><br>

        ● Here we can view disome-seq peaks upstream of the known asparagine (Asn) located stall site (Yanagitani et al., 2011).<br><br>
        ● These disome peaks which are approximately in intervals of ~30 nucleotides represent ribosome queuing.<br><br>
        ● Note the strong monosome-seq peak at the known pause site Asn-256 just before the stop codon.<br><br>

        <h3><h3>Pause Prediction</h3><br><br>

        ● The recommended read length values when comparing both Ribo-seq and disome-seq are Min readlen: 20 and Max readlen: 65<br><br>

        ● <a href="https://eirnabioconnect.com/short/l2e" target="_blank">Click here</a> to see Pause Prediction results which compares arsenite treated Ribo-seq data ‘Monosome_Arsenite’ and arsenite treated disome-seq data ‘Disome_Arsenite’<br><br>

        ● From the table we can easily access a comparison plot by clicking on ‘View’, <a href="https://eirnabioconnect.com/short/l2f" target="_blank">Click here</a> to see an example of one of the comparison plots generated, with the region containing the differential pause site highlighted in yellow. <br><br>

        ● The plot above highlights an area (nucleotide position 2162-2182) that is a predicted pause site in ‘Monosome-arsenite’ and also in
        ‘Disome-arsenite’ but at a greater fold change magnitude in the latter.<br><br>

        We can confirm this by reviewing the subcodon profile for both Arsenite treated Ribo-seq data and Disome-seq data separately:<br><br>
        <a href="https://eirnabioconnect.com/short/l2k" target="_blank">● Subcodon profile for HSP90AA1(ENST00000216281) utilising only arsenite-treated Ribo-seq data</a><br><br>

        <a href="https://eirnabioconnect.com/short/l2n" target="_blank">● Subcodon profile for HSP90AA1(ENST00000216281) utilising only arsenite-treated Disome-seq data</a><br><br>

        <h3>Genome Browser</h3><br><br>

        ● Disome-seq data can also be visualised in the context of a reference genome via this functionality<br><br>
        <a href="https://eirnabioconnect.com/projects/GYLWrxBWUU/comparison_genomebrowser/?chrinput=chr9:99230104-99230742&ambig=F&reverse=F&file_dict=eyJSaWJvc2VxIjogeyJmaWxlX2xpc3QiOiBbMzQ1OSwgMzQ2MCwgMzQ2MSwgMzQ2MiwgMzQ2MywgMzQ2NF0sICJncm91cF9jb2xvciI6ICIjZTAxYjI0In0sICJEaXNvbWVzZXEiOiB7ImZpbGVfbGlzdCI6IFszNzY0LCAzNzY1LCAzNzY2LCAzNzYxLCAzNzYzLCAzNzYyXSwgImdyb3VwX2NvbG9yIjogIiMzNTg0ZTQifX0%3D&hili=99230413-99230433" target="_blank">Click here</a> to see an example where we have visualised SEC61B in reference to GRCh38. A known pause site on the 4th exon before the stop codon (highlighted in yellow), is supported by both monosome and disome data (Mariappan et al., 2010, Ingolia et al., 2011).<br><br>
        ● This consistent disome peak indicates that it is a biologically regulated translational pause. In the case of SEC61B which encodes the
        β-subunit of the Sec61 translocon in the ER, this translational pause ensures the proper timing of nascent peptide insertion into the ER.<br><br>

        <h3>Differential Distribution > Differential Silhouette</h3><br><br>

        ● The recommended read length values when comparing Ribo-seq and disome-seq are Min readlen: 20 and Max readlen: 65<br><br>

        ● <a href="https://eirnabioconnect.com/short/l2z" target="_blank">Click here</a> to see an example output comparing ‘Monosome_arsenite (Ribo-seq, arsenite treated) with ‘Disome_arsenite’ (Disome-seq, arsenite treated).<br><br>
        ● You can click on the data point for the gene PPP2R1A, which is a good example of a gene with a different footprint silhouette between the two conditions
        along its coding region. Clicking will open a comparison plot showing the two different condtions, alteratively you can view the comparison plot directly by <a href="https://eirnabioconnect.com/short/l2E" target="_blank"> clicking here</a>.<br>
        
        ● The change in the footprint silhouette between the two conditions suggests that the disome-seq experiments indicates ribosome stalling
        approximately between nucleotide positions 280 - 380.<br><br>

        ● We can add another layer of evidence to this interpretation via the ‘Pause prediction’ functionality.<br><br>
        ● <a href="https://eirnabioconnect.com/short/l2H" target="_blank">Click here</a> to see an example where we have run this functionality with the parameters ‘Analysis type: Single’ and selecting disome-seq data with arsenite treatment only.<br><br>
        ● Genes can be highlighted by adding list of genes in the 'Highlight genes:' box. One example is e.g. PPP2R1A which shows a strong disome-seq pause within its CDS region.<br><br>

      `,
    },

  ],
};
